import { type FactoryTypeText } from '@common/types/factory';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { type TFunction } from 'i18next';

type TextFormats = {
  nameMaterial?: string;
  nameFactory?: string;
  upgrateResourceText?: string;
  buildingResourceText?: string;
};

function ResourceComponent(
  t: TFunction,
): Record<ResourceTypeText, TextFormats> {
  const resource: Record<ResourceTypeText, TextFormats> = {
    wood: {
      nameMaterial: t('Wood'),
      nameFactory: t('Wood Cutter'),
      upgrateResourceText: t(
        'It gathers wood and ensures a steady flow of this resource for further use in production processes. The higher its level, the more wood it gathers',
      ),
      buildingResourceText: t(
        'Building upgraded! It now produces more wood for processing and construction. Keep up the great work!',
      ),
    },
    cotton: {
      nameMaterial: t('Cotton'),
      nameFactory: t('Cotton Field'),
      upgrateResourceText: t(
        'It extracts cotton and ensures a steady flow of this resource for further use in production processes. The higher its level, the more cotton it extracts',
      ),
      buildingResourceText: t(
        'Building upgraded! It now extracts more cotton for processing and production. Keep up the great work!',
      ),
    },
    bulk: {
      nameMaterial: t('Bulk'),
      nameFactory: t('Bulk Quarry'),
      upgrateResourceText: t(
        'It extracts bulk and ensures a steady flow of this resource for further use in production processes. The higher its level, the more cotton it extracts',
      ),
      buildingResourceText: t(
        'Building upgraded! It now extracts more bulk materials for processing and construction. Keep up the great work!',
      ),
    },
    iron: {
      nameMaterial: t('Iron'),
      nameFactory: t('Iron Mine'),
      upgrateResourceText: '---',
      buildingResourceText: '---',
    },
    oil: {
      nameMaterial: t('Oil'),
      nameFactory: t('Oil Platform'),
      upgrateResourceText: '---',
      buildingResourceText: '---',
    },
    precious: {
      nameMaterial: t('Precious'),
      nameFactory: t('Precious Mine'),
      upgrateResourceText: '---',
      buildingResourceText: '---',
    },
  };

  return resource;
}

function PlantComponent(t: TFunction): Record<PlantTypeText, TextFormats> {
  const plants: Record<PlantTypeText, TextFormats> = {
    lumber: {
      nameMaterial: t('Lumber'),
      nameFactory: t('Sawmill'),
    },
    textiles: {
      nameMaterial: t('Textiles'),
      nameFactory: t('Textile Plant'),
    },
    bricks: {
      nameMaterial: t('Bricks'),
      nameFactory: t('Brick Plant'),
    },
    paper: {
      nameMaterial: t('Paper'),
      nameFactory: t('Paper Plant'),
    },
    steel: {
      nameMaterial: t('Steel'),
      nameFactory: t('Steelworks'),
    },
    plastic: {
      nameMaterial: t('Plastic'),
      nameFactory: t('Polymer Plant'),
    },
    glass: {
      nameMaterial: t('Glass'),
      nameFactory: t('Glass Plant'),
    },
    fuels_and_oils: {
      nameMaterial: t('Fuels and Oils'),
      nameFactory: t('Oil Refinery'),
    },
    microchips: {
      nameMaterial: t('Microchips'),
      nameFactory: t('Microchip Plant'),
    },
  };

  return plants;
}

function FactoryComponent(t: TFunction): Record<FactoryTypeText, TextFormats> {
  const factories: Record<FactoryTypeText, TextFormats> = {
    furniture_factory: {
      nameFactory: t('Furniture Factory'),
    },
    garment_factory: {
      nameFactory: t('Garment Factory'),
    },
    printing_factory: {
      nameFactory: t('Printing Factory'),
    },
    homeware: {
      nameFactory: t('Homeware'),
    },
    toolmaking: {
      nameFactory: t('Toolmaking'),
    },
    childrens_goods: {
      nameFactory: t('Childrens Goods'),
    },
    musical_equipment: {
      nameFactory: t('Musical Equipment'),
    },
    electronics: {
      nameFactory: t('Electronics'),
    },
    machinery: {
      nameFactory: t('Machinery'),
    },
  };

  return factories;
}

const storageTextObj = (t: TFunction): Record<string, string> => ({
  wood: t('Wood'),
  cotton: t('Cotton'),
  bulk: t('Bulk'),
  iron: t('Iron'),
  oil: t('Oil'),
  precious: t('Precious'),
  lumber: t('Lumber'),
  textiles: t('Textiles'),
  bricks: t('Bricks'),
  paper: t('Paper'),
  steel: t('Steel'),
  plastic: t('Plastic'),
  glass: t('Glass'),
  fuels_and_oils: t('Fuels and Oils'),
  microchips: t('Microchips'),
  furniture: t('Furniture'),
  upholstered_furniture: t('Upholstered furniture'),
  garden_furniture: t('Garden furniture'),
  office_furniture: t('Office furniture'),
  clothing: t('Clothing'),
  footwear: t('Footwear'),
  accessories: t('Accessories'),
  sports_equipment: t('Sports equipment'),
  books: t('Books'),
  packaging: t('Packaging'),
  photo_frames: t('Photo frames'),
  stationery: t('Stationery'),
  home_textiles: t('Home textiles'),
  lamps: t('Lamps'),
  tableware: t('Tableware'),
  electric_fireplaces: t('Electric fireplaces'),
  hand_tools: t('Hand tools'),
  garden_tools: t('Garden tools'),
  electronic_tools: t('Electronic tools'),
  machines: t('Machines'),
  soft_toys: t('Soft Toys'),
  board_games: t('Board games'),
  construction_toys: t('Construction toys'),
  electronic_toys: t('Electronic toys'),
  guitar: t('Guitar'),
  electric_guitar: t('Electric guitar'),
  light_music: t('Light music'),
  speakers: t('Speakers'),
  washing_machine: t('Washing machine'),
  laptop: t('Laptop'),
  vr_equipment: t('VR equipment'),
  robotics: t('Robotics'),
  car: t('Car'),
  train: t('Train'),
  airplane: t('Airplane'),
  ship: t('Ship'),
});

export { FactoryComponent, PlantComponent, ResourceComponent, storageTextObj };
